import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { User, CreateUserDTO } from '../../../types/user';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { ROLES } from '../../../config/constants';

interface UserFormProps {
  user?: User;
  onSubmit: (data: CreateUserDTO) => void;
  onClose: () => void;
}

export default function UserForm({ user, onSubmit, onClose }: UserFormProps) {
  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const { control, handleSubmit } = useForm<CreateUserDTO>({
    defaultValues: user
      ? {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName || '',
          mobileNo: user.mobileNo,
          role: user.role,
          status: user.status,
          orgId: selectedOrganizationId || '',
          hasSeenTour: user.hasSeenTour || false,
        }
      : {
          role: 'client_user',
        },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogTitle>{user ? 'Edit User' : 'Add User'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: 'First name is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="First Name"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Email"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="mobileNo"
              control={control}
              render={({ field }) => <TextField {...field} label="Phone (Optional)" fullWidth />}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="role"
              control={control}
              rules={{ required: 'Role is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  label="Role"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                >
                  {ROLES.map(role => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disableElevation>
          {user ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </form>
  );
}
